import * as React from 'react';
import { Translate } from 'react-redux-i18n';
import isEqual from 'lodash.isequal';

import BlurPanel from '../BlurPanel';
import {
	ResourceCreationContent,
	ResourceCreationInputWrapper,
	ResourceCreationInput,
	ResourceCreationInputClear,
	ResourceCreationInputLabel,
	ResourceCreationIcon,
	ResourceCreationError,
	ResourceCreationLoader,
} from './styles';
import Utils from '../../utils';
import keys from '../../utils/keys';
import usePreviousValue from '../../utils/customHooks/usePreviousValue';

type Fields = {
	[name: string]: {
		value: string;
		label?: string;
	}
};

interface ResourceCreationPanelProps {
	isOpen: boolean;
	icon: string;
	fields: Fields;
	onFieldChange: (name: string, value: string) => void;
	onConfirm: (fields: Fields) => void;
	onCancel: () => void;
	errors?: { [k: string]: string | null };
	label?: string;
	autoFocus?: boolean;
}

const ResourceCreationPanel = ({
	isOpen,
	icon,
	fields,
	onFieldChange,
	onConfirm,
	onCancel,
	errors = {},
	label,
	autoFocus = true,
}: ResourceCreationPanelProps) => {
	const inputElement = React.useRef<HTMLInputElement>(null);

	const [windowHeight, setWindowHeight] = React.useState(window.innerHeight);
	const [isLoading, setIsLoading] = React.useState(false);

	const prevErrors = usePreviousValue(errors);
	const hasErrors = Object.values(errors).some(error => !!error);

	const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		onFieldChange(name, value);
	};

	const handleFieldClear = (name: string) => {
		onFieldChange(name, '');
	};

	const handleFieldFocus = (e: React.FocusEvent<HTMLInputElement>): void => {
		fixKeyboardPush(e);
	};

	const fixKeyboardPush = (e: React.FocusEvent<HTMLInputElement>): void => {
		e.preventDefault();
		e.stopPropagation();

		setTimeout(() => {
			window.scrollTo(0, 0);
			document.documentElement.scrollTop = 0;
		}, 0);
	};

	const handleConfirm = () => {
		const isEmptyField = Object.values(fields).some(field => !field.value);

		if (isEmptyField || hasErrors || isLoading) {
			return;
		}

		setIsLoading(true);

		if (inputElement.current) {
			inputElement.current.blur();
		}

		onConfirm(fields);
	};

	React.useEffect(() => {
		const handleResize = () => {
			setWindowHeight(window.innerHeight);
		};

		if (isOpen) {
			// Standard window 'resize' event isn't triggered in webview on iOS13, it should be listened on window.visualViewport
			if (window.visualViewport) {
				window.visualViewport.addEventListener('resize', handleResize);
			} else {
				window.addEventListener('resize', handleResize);
			}
		}

		return () => {
			if (isOpen) {
				if (window.visualViewport) {
					window.visualViewport.removeEventListener('resize', handleResize);
				} else {
					window.removeEventListener('resize', handleResize);
				}
			}
		};
	}, [isOpen]);

	React.useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (keys.isEnter(e.keyCode)) {
				e.preventDefault();
				handleConfirm();
			}
			if (keys.isEsc(e.keyCode)) {
				e.preventDefault();
				onCancel();
			}
		};

		if (isOpen) {
			window.addEventListener('keydown', handleKeyDown);
		}

		return () => {
			if (isOpen) {
				window.removeEventListener('keydown', handleKeyDown);
			}
		};
	}, [isOpen, handleConfirm, onCancel]);

	React.useEffect(() => {
		if (isOpen && autoFocus && inputElement.current) {
			inputElement.current.focus();
		}
	}, [isOpen]);

	React.useEffect(() => {
		const isErrorsChanged = !isEqual(errors, prevErrors);

		if ((!isOpen || isErrorsChanged) && isLoading) {
			setIsLoading(false);
		}
	}, [isOpen, isLoading, errors]);

	return (
		<BlurPanel
			label={label}
			isOpen={isOpen}
			onCancelClick={onCancel}
			onOkClick={handleConfirm}
		>
			{isLoading && !hasErrors &&
				<ResourceCreationLoader>
					{Utils.getSvgIcon('iconset_loading')}
					<Translate value="messages.loading" />
				</ResourceCreationLoader>
			}
			<ResourceCreationContent>
				<ResourceCreationIcon
					key={windowHeight}
					inputBoxNumber={Object.keys(fields).length}
				>
					{Utils.getSvgIcon(icon)}
				</ResourceCreationIcon>
				{Object.entries(fields).map(([name, { value, label }], index) => (
					<ResourceCreationInputWrapper key={`${name}-${index}`}>
						{errors[name]
							? <ResourceCreationError value={errors[name]} />
							: label && <ResourceCreationInputLabel>{label}</ResourceCreationInputLabel>
						}
						<ResourceCreationInput
							type="text"
							name={name}
							value={value}
							ref={index === 0 ? inputElement : undefined}
							data-error={!!errors[name]}
							onFocus={handleFieldFocus}
							onChange={handleFieldChange}
						/>
						{value &&
							<ResourceCreationInputClear
								type="button"
								onClick={() => handleFieldClear(name)}
							>
								{Utils.getSvgIcon('iconset_close-2')}
							</ResourceCreationInputClear>
						}
					</ResourceCreationInputWrapper>
				))}
			</ResourceCreationContent>
		</BlurPanel>
	);
};

export default ResourceCreationPanel;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/ResourceCreationPanel/index.tsx